import { Typography } from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { State, Props, IItem } from "./interfaces";
import { Item } from "./styles";
import { loadEntities } from "./utils";
import { useAppSelector } from "../../custom-hooks/redux/redux";
import { DocumentsFilter } from "../../models/documents";
import { DocumentStatusRef } from "../../models/documents/document-statuses";
import CustomTablePagination from "../../reusable-components/Table/CustomTablePagination/CustomTablePagination";

import { loadDocumentType } from "../../services/document-types";
import { DocumentsApiService } from "../../services/documents/documents-api.service";
import { TransactionsApiService } from "../../services/transactions/transactions-api.service";

import { intlFormatDateShort } from "../../utils/time-utils";
import { getDocumentStatusIcon } from "../Document/document-utils";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";

const DEFAULT_PAGE_SIZE = 10;

const LastLoadedEntities = ({
  entities = "documents",
  handleSelect,
  query,
  legalEntitiesIds = [],
  withoutTransactions = false,
  transactionTypeId,
  setAllowedDocType,
  displayTitle = true,
  displayLegalEntity = false,
  useBoldHeader = false,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.last_uploaded_entities"; // tRP = translation root path

  const { currentUserData } = useAppSelector((state) => state.currentUserReducer);

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    items: [],
    totalItems: 0,
    pageNumber: 0,
  });

  const setSelected = (item: IItem): void => {
    const lastSelectedItem = state.items.filter((item: IItem) => item.isSelected)[0];

    const items = state.items.map((element) => {
      if (element.id === item.id) return { ...element, isSelected: true };

      if (lastSelectedItem && lastSelectedItem.id === element.id) {
        return { ...element, isSelected: false };
      }

      return element;
    });

    setState((state) => ({ ...state, items }));

    handleSelect && handleSelect(item);
  };

  const handleChangePage = (pageNumber: number): void => setState((state) => ({ ...state, pageNumber }));

  const getApiService = useCallback(() => {
    switch (entities) {
      case "documents":
        return new DocumentsApiService();

      case "transactions":
        return new TransactionsApiService();
    }
  }, [entities]);

  const loadEntitiesCallback = useCallback(
    async (query = "", pageNumber = 0, signal: AbortSignal, transactionTypeId: string | undefined) => {
      setState((state) => ({ ...state, loading: true }));

      let allowedDoocumentType;

      if (transactionTypeId) {
        allowedDoocumentType = await loadDocumentType(transactionTypeId);
        setAllowedDocType && setAllowedDocType(!!allowedDoocumentType?.items[0]);
      }

      const { error, items, totalItems } = await loadEntities(
        getApiService(),
        query ? [] : [currentUserData?.user?.id ?? ""],
        query,
        { pageNumber, pageSize: DEFAULT_PAGE_SIZE },
        signal,
        {
          legalEntityIds: legalEntitiesIds,
          withoutTransactions,
          typeIds: allowedDoocumentType?.items[0]?.id ? [allowedDoocumentType?.items[0]?.id] : [],
        } as DocumentsFilter
      );

      if (signal.aborted) return;

      setState((state) => ({
        ...state,
        loading: false,
        error,
        items: items.map((item) => ({ ...item, isSelected: false })),
        totalItems,
      }));
    },
    [getApiService, currentUserData?.user?.id]
  );

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const abortController = new AbortController();

    timer = setTimeout(
      () => loadEntitiesCallback(query, state.pageNumber, abortController.signal, transactionTypeId),
      query ? 500 : 0
    );

    return () => {
      clearTimeout(timer);
      abortController.abort();
    };
  }, [loadEntitiesCallback, query, state.pageNumber]);

  return (
    <LoadingOverlay loading={state.loading}>
      {!state.loading && displayTitle && (
        <Typography variant="subtitle1">
          {query
            ? `${t(
                `${tRP}.${
                  state.items.length
                    ? `title_if_there_are_${entities}_with_query`
                    : `title_if_there_are_no_${entities}_with_query`
                }`
              )} ${query}`
            : `${t(
                `${tRP}.${state.items.length ? `title_if_there_are_${entities}` : `title_if_there_are_no_${entities}`}`
              )} ${legalEntitiesIds ? `and ${t("common_words.legal_entity")}:` : ":"}`}
        </Typography>
      )}
      {!!state.items.length && (
        <>
          <Item useBold={useBoldHeader}>
            {displayLegalEntity && <span>{`${t("common_words.legal_entity")}`}</span>}
            <span>{`${t(`${tRP}.date_column`)}`}</span>
            <span>{`${t(`${tRP}.description_column`)}`}</span>
            <span>{`${t(`${tRP}.status_column`)}`}</span>
            <span>{`${t(`${tRP}.client_vendor`)}`}</span>
          </Item>
          {state.items.map((item, idx) => (
            <Item
              key={idx}
              onClick={() => setSelected(item)}
              selected={item.isSelected}
            >
              {displayLegalEntity && (
                <span>{item.legalEntity?.code ? item.legalEntity?.code : item.legalEntity?.name}</span>
              )}
              <span>{intlFormatDateShort(item.createdAt)}</span>
              <span style={{ textAlign: "start" }}>{item.name ?? item.comment ?? ""}</span>
              <span>{getDocumentStatusIcon(item.status as DocumentStatusRef)}</span>
              <span>{item.client?.name ?? item.vendor?.name ?? ""}</span>
            </Item>
          ))}
          <CustomTablePagination
            totalItems={state.totalItems}
            pageNumber={state.pageNumber}
            pageSize={DEFAULT_PAGE_SIZE}
            rowsPerPageOptions={[DEFAULT_PAGE_SIZE]}
            handleChangePageNumber={handleChangePage}
            outlined
          />
        </>
      )}
    </LoadingOverlay>
  );
};

export default LastLoadedEntities;
